import React, { useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
import {
  PUBLIC_USER_STATUS,
  PUBLIC_USER_STATUS_TO_TEXT,
} from "common/constants";
import { GetPaginatedPublicUsersForAccountQuery } from "../../../generated/graphql";
import { formatDateString } from "../../../utils/dates";
import {
  LegacyTableStateFromURL,
  ManualPaginationConfig,
} from "../../Common/Tables/hooks";
import Table from "../../Common/Tables/Table";
import { StyleVariant, Tag } from "../../Common/Tag";
import { StatusCell, TruncatedText } from "./__styles__/PublicUsersTable";
import { ButtonWrapper, OptionsContainer } from "./__styles__/TeamTable";
import PublicUserActions from "./PublicUserActions";

const PUBLIC_USER_STATUS_TO_TAG_STYLE_VARIANT: Record<
  PUBLIC_USER_STATUS,
  StyleVariant
> = {
  [PUBLIC_USER_STATUS.UNCONFIRMED]: "warning",
  [PUBLIC_USER_STATUS.PENDING]: "warning",
  [PUBLIC_USER_STATUS.ACTIVE]: "default",
  [PUBLIC_USER_STATUS.SUSPENDED]: "neutral",
  [PUBLIC_USER_STATUS.DENIED]: "critical",
} as const;

const LOADING_TEXT = "Loading public website guests";
const NO_DATA_TEXT = "No public website guests found";

export type PublicUser = NonNullable<
  GetPaginatedPublicUsersForAccountQuery["paginatedPublicUsersForAccount"]["data"][number]
>;

type ActionButtonProps = {
  row: PublicUser;
  onUpdate: () => void;
};

const ActionButton = ({ row, onUpdate }: ActionButtonProps) => (
  <OptionsContainer>
    <ButtonWrapper>
      <PublicUserActions publicUser={row} onUpdate={onUpdate} />
    </ButtonWrapper>
  </OptionsContainer>
);

export interface PublicUsersTableProps {
  currentData: Array<PublicUser>;
  loading: boolean;
  manualPaginationConfig: ManualPaginationConfig;
  initialTableState: LegacyTableStateFromURL;
  setTableStateInURL: (state: any) => void;
  onUpdate: () => void;
}

export default ({
  currentData,
  loading,
  manualPaginationConfig,
  initialTableState,
  setTableStateInURL,
  onUpdate,
}: PublicUsersTableProps) => {
  const Name = (row: PublicUser) => {
    return (
      <TruncatedText>
        {row.firstName} {row.lastName}
      </TruncatedText>
    );
  };

  const Status = (row: PublicUser) => {
    return (
      <StatusCell>
        <Tag styleVariant={PUBLIC_USER_STATUS_TO_TAG_STYLE_VARIANT[row.status]}>
          {PUBLIC_USER_STATUS_TO_TEXT[row.status]}
        </Tag>
      </StatusCell>
    );
  };

  const Approver = (row: PublicUser) => {
    if (!row.approvalDecisionBy) return null;
    return `${row.approvalDecisionBy.firstName} ${row.approvalDecisionBy.lastName}`;
  };

  const ApprovalDate = (row: PublicUser) => {
    if (!row.approvalDecisionAt) return null;
    return (
      <TruncatedText>
        {formatDateString({
          format: "MM/DD/YYYY, HH:MM ZZ",
          dateString: row.approvalDecisionAt,
        })}
      </TruncatedText>
    );
  };

  const DenialReason = (row: PublicUser) => {
    if (!row.denialReason) return null;
    return <TruncatedText>{row.denialReason}</TruncatedText>;
  };

  const Group = (row: PublicUser) => {
    const groupNames = row.userGroups.map(group => group.name).join(", ");
    return <TruncatedText>{groupNames}</TruncatedText>;
  };

  const columns = useMemo<Array<ColumnDef<PublicUser>>>(
    () => [
      {
        id: "name",
        header: "Name",
        enableSorting: false,
        cell: ({ row }) => Name(row.original),
      },
      {
        id: "email",
        header: "Email",
        accessorKey: "email",
        enableSorting: false,
      },
      {
        id: "status",
        header: "Status",
        enableSorting: false,
        size: 60,
        minSize: 100,
        cell: ({ row }) => Status(row.original),
      },
      {
        id: "group",
        header: "Group",
        enableSorting: false,
        cell: ({ row }) => Group(row.original),
      },
      {
        id: "denialReason",
        header: "Denial Reason",
        enableSorting: false,
        size: 250,
        cell: ({ row }) => DenialReason(row.original),
      },
      {
        id: "approver",
        header: "Approver",
        enableSorting: false,
        cell: ({ row }) => Approver(row.original),
      },
      {
        id: "approvalDate",
        header: "Approval Date",
        enableSorting: false,
        cell: ({ row }) => ApprovalDate(row.original),
      },
      {
        id: "actions",
        header: "",
        cell: row => (
          <ActionButton onUpdate={onUpdate} row={row.row.original} />
        ),
      },
    ],
    [onUpdate],
  );

  return (
    <Table
      currentData={currentData}
      columns={columns}
      tableStyleDetails={{ hasHighlights: true, hasRowActions: true }}
      loadingDetails={{
        loading,
        loadingText: LOADING_TEXT,
        noDataText: NO_DATA_TEXT,
      }}
      initialState={initialTableState}
      manualPaginationConfig={manualPaginationConfig}
      setTableStateInURL={setTableStateInURL}
    />
  );
};
